import React from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { SidebarProvider } from '../contexts/SidebarContext';
import { isFullBleedRoute } from '../utils/layoutRoutes';
import { OutrisSiteFooter } from './OutrisSiteFooter';
import { PlatformTour } from './PlatformTour';
import { UiLocatorOverlay } from './tour/UiLocatorOverlay';
import { TrialBanner } from './TrialBanner';

export const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const { pathname } = location;
    const fullBleed = isFullBleedRoute(pathname);
    // Site footer on all scrollable shells; canvas routes manage their own chrome.
    const showSiteFooter = !fullBleed;
    const page = children ?? <Outlet />;

    return (
        <SidebarProvider>
            <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
                <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    Skip to main content
                </a>
                <Header />
                <TrialBanner />
                <Toaster position="top-right" richColors closeButton />
                <div className="flex flex-1 min-h-0 h-0 overflow-hidden">
                    <Sidebar />
                    <main
                        id="main-content"
                        className="relative flex flex-1 min-h-0 min-w-0 overflow-hidden bg-background"
                        tabIndex={-1}
                    >
                        {fullBleed ? (
                            <div className="absolute inset-0 flex flex-col min-h-0 overflow-hidden">
                                {page}
                            </div>
                        ) : (
                            <div className="absolute inset-0 overflow-y-auto overflow-x-hidden styled-scrollbar page-scrollbar overscroll-y-contain">
                                <div className="min-h-full flex flex-col">
                                    <div className="flex-1">
                                        {page}
                                    </div>
                                    {showSiteFooter && <OutrisSiteFooter />}
                                </div>
                            </div>
                        )}
                    </main>
                </div>
                <PlatformTour />
                {/* Answers locateUi() from anywhere — guide terms, tour bullets. */}
                <UiLocatorOverlay routeKey={location.key} />
            </div>
        </SidebarProvider>
    );
};
